'use client';

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { getFlagComponent } from '@/shared-ui/icons/flags';
import { cn } from '@/lib/utils';
import { USER_ACTION_ITEM_CLASSES } from './item-classes';

// Inert placeholder until the currency selector ships.
const baseClasses = cn(USER_ACTION_ITEM_CLASSES, 'cursor-default hover:opacity-100');

/**
 * Display-only currency indicator: the active currency code plus the current locale's flag (resolved by
 * region via the flags registry; locales without a drawn flag show the code alone). Visibility is the
 * consumer's call via `className` — the header hides it on mobile; the burger menu can mount it visible.
 */
export const CurrencyDisplay = ({ className }: { className?: string }): ReactElement | null => {
    const { t } = useTranslation('header');
    const { currency, locale } = useSite();

    if (!currency) {
        return null;
    }

    const Flag = getFlagComponent(locale?.id);

    return (
        <span className={cn(baseClasses, className)} aria-label={t('userActions.currency')}>
            {Flag ? <Flag className="shrink-0 rounded-[2px]" /> : null}
            <span>{currency}</span>
        </span>
    );
};
